import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { Heart, Award, Sparkles, Flame, Lightbulb, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = [
  { kind: "heart", Icon: Heart, label: "Love" },
  { kind: "clap", Icon: Award, label: "Clap" },
  { kind: "wow", Icon: Sparkles, label: "Wow" },
  { kind: "fire", Icon: Flame, label: "Fire" },
  { kind: "idea", Icon: Lightbulb, label: "Idea" },
];

export default function LiveWidget({ portalToken, webinarId }) {
  const [state, setState] = useState({ reactions: {}, questions_count: 0 });
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const [busyReaction, setBusyReaction] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/public/live/${webinarId}/state`);
      setState(data);
    } catch {/* silent */}
  }, [webinarId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 6000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const react = async (kind) => {
    setBusyReaction(kind);
    try {
      await axios.post(`${API_BASE}/public/live/reaction`, { portal_token: portalToken, kind });
      setState((s) => ({ ...s, reactions: { ...s.reactions, [kind]: (s.reactions[kind] || 0) + 1 } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Try again"); }
    finally { setTimeout(() => setBusyReaction(null), 400); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim() || q.trim().length < 2) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE}/public/live/question`, { portal_token: portalToken, question: q.trim() });
      setQ(""); toast.success("Question sent to the host");
      setState((s) => ({ ...s, questions_count: s.questions_count + 1 }));
    } catch (e) { toast.error(e.response?.data?.detail || "Try again"); }
    finally { setSending(false); }
  };

  return (
    <div className="vp-glass p-5 space-y-5" data-testid="live-widget">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-[#E02D3C] animate-pulse" />
        <div className="vp-label" style={{ color: "#E02D3C" }}>Live · engage with the host</div>
      </div>

      {/* Reactions */}
      <div>
        <div className="text-xs text-[#747680] mb-2">Send a reaction</div>
        <div className="flex flex-wrap gap-2">
          {REACTIONS.map(({ kind, Icon, label }) => (
            <button
              key={kind}
              onClick={() => react(kind)}
              disabled={busyReaction === kind}
              className={"px-3 py-2 rounded-full border border-[#E4E6EC] bg-white flex items-center gap-2 text-sm hover:border-[#0B5CFF] hover:bg-[#0B5CFF]/5 transition-all " + (busyReaction === kind ? "scale-110" : "")}
              data-testid={TIDS.liveReactionBtn + "-" + kind}
            >
              <Icon className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
              <span>{label}</span>
              {state.reactions[kind] > 0 && (
                <span className="text-xs text-[#747680] font-mono">{state.reactions[kind]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="vp-divider" />

      {/* Question */}
      <div>
        <div className="text-xs text-[#747680] mb-2 flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
          Ask the host · {state.questions_count} question{state.questions_count !== 1 ? "s" : ""} so far
        </div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
          <input
            className="vp-input flex-1"
            placeholder="What do you want to know?"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            maxLength={500}
            data-testid={TIDS.liveQuestionInput}
          />
          <button
            type="submit"
            disabled={sending || q.trim().length < 2}
            className="vp-btn-primary sm:w-auto inline-flex items-center gap-2 justify-center"
            data-testid={TIDS.liveQuestionSubmit}
          >
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
}
